import React, { useState, useEffect } from 'react';
import { Button, Modal, Field, FieldLabel, FieldDescription, FieldError } from '../ui';
import { Booking } from '@court-sync/shared';

interface RefundRequestModalProps {
  isOpen: boolean;
  booking: Booking | null;
  lessonName?: string;
  amount?: number;
  currency?: string;
  isLoading?: boolean;
  /** Prepopulated reason (e.g. when arriving from term lesson cancelled email) */
  defaultReason?: string;
  onCancel: () => void;
  onConfirm: (reason?: string) => Promise<void>;
}

export const RefundRequestModal: React.FC<RefundRequestModalProps> = ({
  isOpen,
  booking,
  lessonName,
  amount,
  currency,
  isLoading = false,
  defaultReason = '',
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState(defaultReason);

  useEffect(() => {
    if (isOpen && defaultReason) {
      setReason(defaultReason);
    }
  }, [isOpen, defaultReason]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');
      await onConfirm(reason);
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request refund');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onCancel();
  };

  if (!isOpen || !booking) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Request Refund" maxWidth="sm">
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
          <p className="font-medium">Refund Details</p>
          {lessonName && <p>Lesson: {lessonName}</p>}
          {amount && currency && (
            <p>
              Amount: {currency}
              {(amount / 100).toFixed(2)}
            </p>
          )}
        </div>

        <Field>
          <FieldLabel>Reason for Refund (Optional)</FieldLabel>
          <FieldDescription>
            Help us improve by letting us know why you're requesting a refund
          </FieldDescription>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError('');
            }}
            placeholder="E.g., Cannot attend the rescheduled time, prefer to cancel, etc."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            disabled={submitting || isLoading}
          />
        </Field>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          <p className="font-medium mb-1">Please Note:</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>Refund requests will be processed within 5-7 business days</li>
            <li>Refunds are issued to the original payment method</li>
            <li>Academy owner will review your request</li>
          </ul>
        </div>

        {error && <FieldError>{error}</FieldError>}

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitting || isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || isLoading}
            isLoading={submitting || isLoading}
          >
            Request Refund
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RefundRequestModal;
